import type { MapSurface } from '../types';
import { MAIN_MAP_SURFACE } from '../types';

/**
 * Counts live Esri `MapView` instances, split by surface.
 *
 * This is the app's own view of how many maps exist, which is more trustworthy than
 * the DOM-derived canvas/context counts: it says how many MapViews *we* believe we
 * created and never tore down.
 *
 * Two things it is meant to answer:
 *   1. Does the main map coexist with embedded maps? In First Responder the main map
 *      stays mounted for the whole session while a routed page can also mount an
 *      embedded map, so more than one live MapView is expected there and not in CAD.
 *   2. Are MapViews being leaked? A `liveMainMapCount` that only ever climbs means
 *      views are created but never destroyed.
 *
 * `createdMainMapCount` is monotonic, so `created - live` tells you how many were
 * actually cleaned up.
 *
 * The main/embedded rollup fields are kept as-is (production queries already use
 * them); `liveMapsBySurface` is the finer-grained addition that says *which* UI holds
 * the live views.
 */

let liveMainMapCount = 0;
let liveEmbeddedMapCount = 0;
let createdMainMapCount = 0;
let createdEmbeddedMapCount = 0;
const liveBySurface = new Map<MapSurface, number>();

export type MapInstanceCounts = {
    liveMainMapCount: number;
    liveEmbeddedMapCount: number;
    createdMainMapCount: number;
    createdEmbeddedMapCount: number;
    /** Live MapView count per surface, e.g. `["1x main-map", "2x location-select"]`.
     * Omitted when nothing is live. */
    liveMapsBySurface?: string[];
};

export function registerMapView(surface: MapSurface): void {
    if (surface === MAIN_MAP_SURFACE) {
        liveMainMapCount++;
        createdMainMapCount++;
    } else {
        liveEmbeddedMapCount++;
        createdEmbeddedMapCount++;
    }
    liveBySurface.set(surface, (liveBySurface.get(surface) ?? 0) + 1);
}

export function unregisterMapView(surface: MapSurface): void {
    if (surface === MAIN_MAP_SURFACE) {
        liveMainMapCount = Math.max(0, liveMainMapCount - 1);
    } else {
        liveEmbeddedMapCount = Math.max(0, liveEmbeddedMapCount - 1);
    }
    const remaining = (liveBySurface.get(surface) ?? 0) - 1;
    if (remaining > 0) {
        liveBySurface.set(surface, remaining);
    } else {
        liveBySurface.delete(surface);
    }
}

export function getMapInstanceCounts(): MapInstanceCounts {
    const counts: MapInstanceCounts = {
        liveMainMapCount,
        liveEmbeddedMapCount,
        createdMainMapCount,
        createdEmbeddedMapCount,
    };
    if (liveBySurface.size > 0) {
        counts.liveMapsBySurface = Array.from(liveBySurface.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([surface, count]) => `${count}x ${surface}`);
    }
    return counts;
}
